import React, { useId } from 'react';
import clsx from 'clsx';
import ModalShell from './ModalShell';

export const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-colors duration-150 touch-manipulation';

  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm focus-visible:ring-blue-500',
    secondary: 'bg-gray-700 text-white hover:bg-gray-800 shadow-sm focus-visible:ring-gray-500',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm focus-visible:ring-emerald-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm focus-visible:ring-red-500',
    warning: 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm focus-visible:ring-amber-500',
    outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm focus-visible:ring-blue-500',
  };

  const sizeClasses = {
    small: 'px-2.5 py-1 text-xs sm:text-sm min-h-[28px]',
    medium: 'px-3 py-1.5 text-sm min-h-[32px]',
    large: 'px-4 py-2 text-sm sm:text-base min-h-[40px]',
  };

  return (
    <button
      className={clsx(
        baseClasses,
        variantClasses[variant] || variantClasses.primary,
        sizeClasses[size] || sizeClasses.medium,
        { 'opacity-50 cursor-not-allowed': disabled || loading },
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
      type={type}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  );
};

const fieldClasses = (error) => clsx(
  'block w-full px-3 py-2 sm:py-1.5 border rounded-lg bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 text-base sm:text-sm transition-colors duration-150 disabled:bg-gray-100 disabled:text-gray-500',
  error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/30' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/30'
);

const FieldLabel = ({ htmlFor, label, required }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
    {label}
    {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
  </label>
);

export const Input = ({
  label,
  error,
  required = false,
  className = '',
  id,
  ...props
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  return (
    <div className={className}>
      {label && <FieldLabel htmlFor={inputId} label={label} required={required} />}
      <input
        id={inputId}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={clsx(fieldClasses(error), 'min-h-[44px] sm:min-h-[40px]')}
        {...props}
      />
      {error && <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export const Select = ({
  label,
  error,
  required = false,
  children,
  className = '',
  id,
  ...props
}) => {
  const generatedId = useId();
  const selectId = id || generatedId;
  return (
    <div className={className}>
      {label && <FieldLabel htmlFor={selectId} label={label} required={required} />}
      <select
        id={selectId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={clsx(fieldClasses(error), 'min-h-[44px] sm:min-h-[40px]')}
        {...props}
      >
        {children}
      </select>
      {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export const Textarea = ({
  label,
  error,
  required = false,
  className = '',
  id,
  ...props
}) => {
  const generatedId = useId();
  const textareaId = id || generatedId;
  return (
    <div className={className}>
      {label && <FieldLabel htmlFor={textareaId} label={label} required={required} />}
      <textarea
        id={textareaId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={clsx(fieldClasses(error), 'resize-y')}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export const Card = ({ children, className = '', ...props }) => {
  return (
    <div
      className={clsx(
        'bg-white overflow-hidden border border-gray-200 shadow-sm rounded-xl',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const Badge = ({
  children,
  variant = 'default',
  className = '',
  ...props
}) => {
  const variantClasses = {
    default: 'bg-gray-100 text-gray-700 ring-gray-200',
    success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    danger: 'bg-red-50 text-red-700 ring-red-200',
    warning: 'bg-amber-50 text-amber-800 ring-amber-200',
    info: 'bg-blue-50 text-blue-700 ring-blue-200',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset',
        variantClasses[variant] || variantClasses.default,
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
};

// Callers render their own header/close button inside `children` (kept for compatibility)
export const Modal = ({ isOpen, onClose, children, maxWidth = 'max-w-2xl' }) => (
  <ModalShell isOpen={isOpen} onClose={onClose} panelClassName={maxWidth}>
    <div className="flex-1 overflow-y-auto overscroll-contain px-4 pt-3 pb-3 sm:p-4">
      {children}
    </div>
  </ModalShell>
);

export const LoadingSpinner = ({ size = 'medium' }) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-8 w-8',
    large: 'h-12 w-12',
  };

  return (
    <div className="flex justify-center" role="status" aria-label="Loading">
      <div className={`animate-spin rounded-full border-2 border-gray-200 border-t-blue-600 ${sizeClasses[size] || sizeClasses.medium}`}></div>
    </div>
  );
};

export const EmptyState = ({ icon, title, description, action }) => {
  return (
    <div className="text-center py-6">
      <div className="mx-auto h-10 w-10 text-gray-400 mb-2">
        {icon}
      </div>
      <h3 className="mt-2 text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
};

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalItems,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="bg-white px-3 py-2 flex items-center justify-between border-t border-gray-200">
      <div className="flex-1 flex justify-between sm:hidden">
        <Button variant="outline" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
          Previous
        </Button>
        <Button variant="outline" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          Next
        </Button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
        </p>
        <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px" aria-label="Pagination">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-2.5 py-1.5 rounded-l-lg border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              type="button"
              key={page}
              onClick={() => onPageChange(page)}
              aria-current={page === currentPage ? 'page' : undefined}
              className={clsx(
                'relative inline-flex items-center px-3 py-1.5 border text-sm font-medium',
                page === currentPage
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              {page}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-2.5 py-1.5 rounded-r-lg border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </nav>
      </div>
    </div>
  );
};
